import type { SeenMedplumClient } from '@seen/core/client'
import {
  refreshMedplumIfExpired,
  type SeenAiApi,
  useMedplum,
} from '@seen/react'
import { hc, parseResponse } from 'hono/client'
import { useMemo } from 'react'
import type { AppType } from '#src/server/app.ts'

export type PortalAiClient = ReturnType<typeof hc<AppType>>

export function createAuthFetch(medplum?: SeenMedplumClient): typeof fetch {
  return async (input, init) => {
    const headers = new Headers(init?.headers)
    if (medplum) {
      await refreshMedplumIfExpired(medplum)
      headers.set('Authorization', `Bearer ${medplum.getAccessToken()}`)
      if (medplum.traceId) {
        headers.set('X-Trace-ID', medplum.traceId)
      }
    }
    return fetch(input, { ...init, headers })
  }
}

export function createPortalAiClient(
  medplum?: SeenMedplumClient
): PortalAiClient {
  return hc<AppType>('', { fetch: createAuthFetch(medplum) })
}

export function usePortalAiClient(): PortalAiClient {
  const medplum = useMedplum()
  return useMemo(() => createPortalAiClient(medplum), [medplum])
}

// The `SeenAiApi` annotation is the conformance check between the portal's
// RPC-inferred types and the shared @seen/request-types contract: if the
// server's wire shape drifts from the contract, this fails to compile.
export function createSeenAiApi(client: PortalAiClient): SeenAiApi {
  return {
    suggestIcd10: (request) =>
      parseResponse(client.api.suggestIcd10.$post({ json: request })),
    suggestAppointmentSlots: (request) =>
      parseResponse(
        client.api.suggestAppointmentSlots.$post({ json: request })
      ),
    recordAppointmentSlotSelected: (request) =>
      parseResponse(
        client.api.appointmentSlotSelected.$post({ json: request })
      ).then(() => undefined),
    recordAppointmentSlotNotSelected: (request) =>
      parseResponse(
        client.api.appointmentSlotNotSelected.$post({ json: request })
      ).then(() => undefined),
    recordSuggestionFeedback: (request) =>
      parseResponse(
        client.api.suggestionFeedback.$post({ json: request })
      ).then(() => undefined),
    refreshQueuedEditsSummary: (request) =>
      parseResponse(
        client.api.refreshQueuedEditsSummary.$post({ json: request })
      ),
    rafScore: (request) =>
      parseResponse(client.api.rafScore.$post({ json: request })),
    icd10RafWeights: (request) =>
      parseResponse(client.api.icd10RafWeights.$post({ json: request })),
    evaluateSchedulingRules: (request) =>
      parseResponse(
        client.api.schedulingRules.evaluate.$post({ json: request })
      ),
    suggestEngineSchedulingSlots: (request) =>
      parseResponse(client.api.schedulingRules.slots.$post({ json: request })),
  }
}
